import React, { useMemo, useState } from 'react';
import { MenuItem, MenuSeparator } from '../../../components/Menu';
import downloadAllIcon from '../../../icons/non-prefilled/16x16/downloadAll.svg';
import mentionIcon from '../../../icons/non-prefilled/16x16/mention.svg';
import trashIcon from '../../../icons/non-prefilled/16x16/trash.svg';
import useIsPublicView from '../../../utils/useIsPublicView';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill } from '../../QuillContext';
import { useDownloadAttachments } from '../../attachment/DownloadAttachmentsProvider';
import type { AttachmentFile } from '../../formats/Attachment';
import Attachment from '../../formats/Attachment';
import type { Blot } from '../../types';
import replaceBlotWith from '../../utils/replaceBlotWith';
import AddAttachmentMenuItem from './AddAttachmentMenuItem';
import FloatingToolbar from './FloatingToolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarEditButton from './buttons/ToolbarEditButton';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import useBlotHighlightedClass from './useBlotHighlightedClass';
import { isAttachmentUploaded } from '../../../utils/AttachmentUploader';

interface BaseAttachmentToolbarProps {
  hoveredBlot: Attachment;
  publicPost?: boolean;
  handleDownloadAll: (() => Promise<void>) | null;
  setPersist?: (persist: boolean) => void;
}

export const BaseAttachmentToolbar = ({
  hoveredBlot,
  publicPost,
  handleDownloadAll,
  setPersist,
}: BaseAttachmentToolbarProps) => {
  const { quill, readOnly } = useQuill();
  const [isPreparing, setIsPreparing] = useState(false);
  useBlotHighlightedClass(hoveredBlot);
  const formats = useMemo(() => hoveredBlot.formats(), [hoveredBlot]);

  if (readOnly && publicPost) {
    if (!handleDownloadAll) return null;

    return (
      <ToolbarButtonGroup>
        <ToolbarButton
          tooltip={isPreparing ? 'Preparing...' : 'Download all (zip)'}
          disabled={isPreparing}
          icon={downloadAllIcon}
          onClick={() => {
            setIsPreparing(true);
            handleDownloadAll().finally(() => setIsPreparing(false));
          }}
        />
      </ToolbarButtonGroup>
    );
  }

  return (
    <>
      {readOnly && (
        <ToolbarButtonGroup>
          <ToolbarEditButton targetBlot={hoveredBlot} />
        </ToolbarButtonGroup>
      )}
      <ToolbarCommentButtonGroup
        blot={hoveredBlot}
        existingComment={formats.comment}
        onReactionPickerOpenChange={setPersist}
      />
      {(handleDownloadAll || !readOnly) && (
        <ToolbarButtonGroup>
          <ToolbarOverflowMenuButton onMenuOpenChange={setPersist}>
            {(onClose) => (
              <>
                {!readOnly && (
                  <AddAttachmentMenuItem
                    onUpload={(e) => {
                      hoveredBlot.appendAttachment(
                        Array.from(e.currentTarget.files ?? []),
                      );
                      onClose();
                    }}
                  />
                )}
                {!readOnly && quill && (
                  <MenuItem
                    icon={mentionIcon}
                    onClick={() => {
                      replaceBlotWith(
                        quill,
                        hoveredBlot,
                        {
                          'attachment-mention':
                            hoveredBlot.getAttachmentFiles(),
                        },
                        hoveredBlot.formats(),
                      );
                    }}
                  >
                    Display as mention
                  </MenuItem>
                )}
                {handleDownloadAll && (
                  <MenuItem
                    icon={downloadAllIcon}
                    onClick={() => {
                      handleDownloadAll();
                      onClose();
                    }}
                  >
                    Download all (zip)
                  </MenuItem>
                )}
                {!readOnly && (
                  <>
                    <MenuSeparator />
                    <MenuItem
                      icon={trashIcon}
                      onClick={() => hoveredBlot.remove()}
                      danger
                    >
                      Delete all files
                    </MenuItem>
                  </>
                )}
              </>
            )}
          </ToolbarOverflowMenuButton>
        </ToolbarButtonGroup>
      )}
    </>
  );
};

type Context = {
  visibleAttachments: AttachmentFile[];
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Context,
  Attachment
> = {
  getHoveredBlot(blot: Blot) {
    return blot.statics.blotName === Attachment.blotName &&
      blot instanceof Attachment
      ? blot
      : null;
  },
  getContext(blot: Attachment) {
    return {
      visibleAttachments: blot.getVisibleAttachments(),
    };
  },
};

const AttachmentToolbar = () => {
  const downloadAttachments = useDownloadAttachments();
  const isPublicView = useIsPublicView();
  const { quill } = useQuill();

  const {
    bounds,
    hoveredBlot,
    context: { visibleAttachments } = {
      visibleAttachments: [],
    },
    setPersist,
  } = useHoveredBlotListener(quill, hoveredBlotListenerSetting);

  const downloadableAttachments =
    visibleAttachments.filter(isAttachmentUploaded);

  const isHidden =
    // Attachment upload was failed, or other collaborator is uploading.
    !visibleAttachments.length ||
    (isPublicView && !downloadableAttachments.length);

  const handleDownloadAll =
    downloadAttachments && downloadableAttachments.length > 0
      ? () => downloadAttachments(downloadableAttachments)
      : null;

  return (
    <FloatingToolbar
      testId="attachmentToolbar"
      bounds={isHidden ? null : bounds}
    >
      {hoveredBlot instanceof Attachment && (
        <BaseAttachmentToolbar
          hoveredBlot={hoveredBlot}
          publicPost={isPublicView}
          handleDownloadAll={handleDownloadAll}
          setPersist={setPersist}
        />
      )}
    </FloatingToolbar>
  );
};

export default AttachmentToolbar;
